/**
 * Runtime monitoring.
 *
 * Sentry is initialised only when a DSN is supplied at build time, so the game
 * runs identically with no monitoring configured — an unavailable service must
 * never be a dependency of the product.
 *
 * The context we attach is chosen so that a report is actually actionable:
 * seed, lifeId, runId, age and life stage together reproduce the exact state
 * that failed. Expected game events are never reported; only genuine faults.
 */

import * as Sentry from '@sentry/react';
import type { PlayerState } from '@core/types';

const DSN = import.meta.env['VITE_SENTRY_DSN'] as string | undefined;
const RELEASE = (import.meta.env['VITE_RELEASE'] as string | undefined) ?? 'dev';
const ENVIRONMENT = (import.meta.env['VITE_ENV'] as string | undefined) ?? (import.meta.env.DEV ? 'development' : 'production');

let enabled = false;

export function initMonitoring(): void {
  if (!DSN) return;
  Sentry.init({
    dsn: DSN,
    release: RELEASE,
    environment: ENVIRONMENT,
    // No performance tracing: this is a local-only game and the volume would
    // be noise rather than signal.
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event) {
      // Never ship save contents or player-entered names off-device.
      if (event.extra) delete event.extra['state'];
      return event;
    },
  });
  enabled = true;
}

/** Attach the minimum context needed to reproduce a failure from a report. */
export function setGameContext(state: PlayerState): void {
  if (!enabled) return;
  Sentry.setTags({
    lifeStage: state.stage,
    careerStatus: state.career.status,
    burnoutStage: state.burnout.stage,
    marketRegime: state.market.regime,
    saveVersion: String(state.saveVersion),
    contentVersion: state.contentVersion,
  });
  Sentry.setContext('life', {
    seed: state.rng.seed,
    rngCursor: state.rng.cursor,
    lifeId: state.lifeId,
    runId: state.runId,
    age: state.age,
    nodeId: state.nodeId,
    decisions: state.decisions.length,
    pendingEvents: state.pendingEvents.join(','),
  });
}

/** Report a genuine fault. Expected game outcomes must not come through here. */
export function reportFault(error: unknown, where: string): void {
  if (!enabled) {
    console.error(`[a-life] ${where}`, error);
    return;
  }
  Sentry.captureException(error, { tags: { where } });
}

export const monitoringEnabled = (): boolean => enabled;
